import { forwardRef, useId } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import "./TextField.css";

/**
 * TextField — 신형 floating-label text field (닷컴/NCMS `.c-input-bomE` 패턴).
 *
 * DOM 계약(중요): input 이 먼저, label 이 바로 다음 형제로 와야 floating CSS(sibling
 * selector)가 동작한다.
 *
 *   <div class="c-input-bomE">
 *     <input class="input-animation" id placeholder />
 *     <label class="label-move" for><span class="label-text">Label</span></label>
 *   </div>
 *
 * 상태 정책(판정/validation/placeholder 노출)은 프로젝트가 결정한다. 본 컴포넌트는
 * 표현 class 만 매핑한다: error→.my-warn, 고정→.is-fix, 값있음→.is-filled.
 */
export type TextFieldTone = "white" | "warm";
export type TextFieldStatus = "default" | "error";

/* Figma icon_error_14 (2659:15173) — assistive 에러 아이콘. 빨강 외곽 원(링) + 느낌표,
 * 단일 path(도넛 구멍 = nonzero winding). 색은 currentColor 로 --field-assistive-error 상속. */
function ErrorIcon() {
  return (
    <svg
      className="bomE-warning__icon"
      viewBox="0 0 14 14"
      width="14"
      height="14"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M7 0C3.15 0 0 3.15 0 7C0 10.85 3.15 14 7 14C10.85 14 14 10.85 14 7C14 3.15 10.85 0 7 0ZM7 12.6C3.9375 12.6 1.4 10.0625 1.4 7C1.4 3.9375 3.9375 1.4 7 1.4C10.0625 1.4 12.6 3.9375 12.6 7C12.6 10.0625 10.0625 12.6 7 12.6ZM6.3 10.5H7.7V9.1H6.3V10.5ZM6.3 7.7H7.7V3.5H6.3V7.7Z"
        fill="currentColor"
      />
    </svg>
  );
}

interface TextFieldOwnProps {
  /** 플로팅 라벨 텍스트(.label-text). */
  label?: ReactNode;
  /** 필수 표시(.c-required). */
  required?: boolean;
  tone?: TextFieldTone;
  status?: TextFieldStatus;
  /** 라벨을 항상 떠 있게 고정(.is-fix) — 드롭다운식. 기본 false(인풋식). */
  alwaysFloat?: boolean;
  /** 좌측 아이콘(Figma showLeadingIcon). */
  leadingIcon?: ReactNode;
  /** 우측 아이콘(삭제/검증 등). */
  trailingIcon?: ReactNode;
  /** 하단 메시지: error 면 .bomE-warning, 아니면 .c-input-bomE__info. */
  assistiveText?: ReactNode;
  /** 컨테이너(.c-input-bomE)에 붙는 className. */
  className?: string;
}

export type TextFieldProps = TextFieldOwnProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, keyof TextFieldOwnProps>;

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  function TextField(
    {
      label,
      required = false,
      tone = "white",
      status = "default",
      alwaysFloat = false,
      leadingIcon,
      trailingIcon,
      assistiveText,
      className,
      id,
      value,
      placeholder,
      disabled,
      ...inputProps
    },
    ref,
  ) {
    const reactId = useId();
    const inputId = id ?? reactId;
    // controlled value 가 있으면 채움 여부를 class 로 반영(.is-filled).
    const filled =
      value != null && value !== "" ? String(value).length > 0 : false;

    const cls = [
      "c-input-bomE",
      tone === "warm" && "c-input-bomE--warm",
      status === "error" && "my-warn",
      alwaysFloat && "is-fix",
      filled && "is-filled",
      disabled && "is-disabled",
      leadingIcon && "has-leading",
      trailingIcon && "has-trailing",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className={cls}>
        {leadingIcon && (
          <span className="c-input-bomE__leading">{leadingIcon}</span>
        )}
        <input
          ref={ref}
          id={inputId}
          className="input-animation"
          /* :placeholder-shown 으로 빈값 판정이 되도록 placeholder 를 항상 둔다. */
          placeholder={placeholder ?? " "}
          value={value}
          disabled={disabled}
          aria-invalid={status === "error" || undefined}
          {...inputProps}
        />
        {label != null && (
          <label className="label-move" htmlFor={inputId}>
            <span className="label-text">
              {label}
              {required && <span className="c-required">*</span>}
            </span>
          </label>
        )}
        {trailingIcon && (
          <span className="c-input-bomE__trailing">{trailingIcon}</span>
        )}
        {assistiveText != null && (
          <p className={status === "error" ? "bomE-warning" : "c-input-bomE__info"}>
            {status === "error" && <ErrorIcon />}
            <span className="bomE-warning__text">{assistiveText}</span>
          </p>
        )}
      </div>
    );
  },
);

export default TextField;
